import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { ArticleDetailScreen } from '@/common/ArticleDetailScreen';
import { NotFoundScreen } from '@/common/ErrorScreen';

/**
 * A follow-up tutorial single: title, date and body, with no sidebar, comments,
 * related posts or category chips, as in legacy.
 *
 * Unlike legacy's `history.back()`, the back arrow goes to the unfiltered
 * archive, as on the other detail routes.
 */
function FollowUpTutorialDetail(): ReactElement | null {
  const { t } = useTranslation();
  const { slug } = Route.useParams();
  const { data, isError, isPending } = api.getFollowUpTutorial.useQuery({
    path: { slug },
  });

  // Legacy 404s server-side, so a refusal is the not-found screen. Any error
  // gets it, not just a 404, as on the sibling detail routes.
  if (isError) return <NotFoundScreen />;
  // Required: `ArticleDetailScreen` renders `notFoundMessage` whenever
  // `article` is falsy, so passing `data` while it loads would flash "Page not
  // found" on every successful load.
  if (isPending || data === undefined) return null;

  return (
    <ArticleDetailScreen
      article={{
        title: data.title,
        content: data.content,
        // Already formatted by the server, e.g. "December 2, 2022".
        date: data.published_on,
        // The author avatar renders as on the other four kinds, as in legacy.
        authorName: data.author_name,
        authorAvatarUrl: data.author_avatar_url,
      }}
      backPath="/follow-up-tutorials/"
      // Unreachable behind the two guards above, but the prop is required.
      notFoundMessage={t('wbs.page-not-found')}
      // `showDate` stays at its default: legacy's single shows the date.
    />
  );
}

export const Route = createFileRoute('/_app/follow-up-tutorials/$slug')({
  component: FollowUpTutorialDetail,
});
